/**
 * /plugins/new — the first draft of a plugin: its name, its kind, where it
 * runs, and what it says about itself. On create the signing secret is shown
 * once, before the workspace opens.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../services/api';
import { Button, useToast } from '../../components/common';
import { Field, KindPicker, SecretOnce, WhereItRuns } from './bits';
import { detailsGaps, errorsAt, toErrors } from './manifest';
import './PluginWorkspace.css';

const blank = {
  name: '',
  description: '',
  usage_guide: '',
  execution_type: 'endpoint',
  endpoint_url: '',
  plugin_endpoint_base_url: '',
  mcp_server_url: '',
  request_spec: { method: 'GET', url: '' },
  response_map: {},
  tools: [],
};

/** The register body for this kind: only what the kind uses. */
export function registerBody(d) {
  const body = {
    name: d.name.trim(),
    description: d.description.trim(),
    execution_type: d.execution_type,
    deployment_mode: 'incubator',
  };
  if (d.usage_guide.trim()) body.usage_guide = d.usage_guide.trim();
  if (d.execution_type === 'endpoint') {
    if (d.endpoint_url.trim()) body.endpoint_url = d.endpoint_url.trim();
    if (d.plugin_endpoint_base_url.trim()) body.plugin_endpoint_base_url = d.plugin_endpoint_base_url.trim();
  } else if (d.execution_type === 'mcp') {
    body.mcp_server_url = d.mcp_server_url.trim();
    if (d.tools.length) body.tools = d.tools;
  } else {
    body.request_spec = d.request_spec;
    body.response_map = d.response_map;
  }
  return body;
}

export default function PluginCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [created, setCreated] = useState(null); // {id, secret}

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const gaps = useMemo(() => detailsGaps(draft), [draft]);
  const at = (k) => errorsAt(errors, k).map((e) => e.message);

  const create = async () => {
    setBusy(true);
    setErrors([]);
    try {
      const r = await api.registerPlugin(registerBody(draft));
      const p = r?.plugin || r;
      const secret = r?.plugin_secret || p?.plugin_secret;
      if (secret) setCreated({ id: p.id, secret });
      else {
        toast.success('Created.');
        navigate(`/plugins/${p.id}?step=tools`, { replace: true });
      }
    } catch (e) {
      setErrors(toErrors(e.body?.errors || e));
    }
    setBusy(false);
  };

  if (created) {
    return (
      <div className="pw pw--page standalone-page ofself-page-fade">
        <h1 className="pw__title">{draft.name.trim()}</h1>
        <SecretOnce
          secret={created.secret}
          doneLabel="Go to its tools"
          onDone={() => navigate(`/plugins/${created.id}?step=tools`, { replace: true })}
        />
      </div>
    );
  }

  const unplaced = errors.filter((e) => !e.field);

  return (
    <div className="pw pw--page standalone-page ofself-page-fade">
      <header className="pw__head">
        <button type="button" className="pw__back" onClick={() => navigate('/plugins')}>All plugins</button>
        <h1 className="pw__title">New plugin</h1>
      </header>

      <section className="pw__sec">
        <div className="pw__fields">
          <Field label="Name" errors={at('name')}>
            <input className="pw__in" value={draft.name} disabled={busy} autoFocus
                   placeholder="Chess.com" onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Kind">
            <KindPicker value={draft.execution_type} disabled={busy}
                        onChange={(v) => { set({ execution_type: v }); setErrors([]); }} />
          </Field>
        </div>
      </section>

      <section className="pw__sec">
        <h2 className="pw__h">Where it runs</h2>
        <WhereItRuns key={draft.execution_type} draft={draft} set={set} disabled={busy} errors={errors}
                     onDiscovered={(tools) => set({ tools: tools.map(({ input_schema: _s, ...t }) => t) })} />
        {draft.execution_type === 'mcp' && draft.tools.length ? (
          <ul className="pw__found">
            {draft.tools.map((t) => (
              <li key={t.tool_id}><span>{t.name}</span>{t.description ? <span className="pw__muted"> · {t.description}</span> : null}</li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="pw__sec">
        <Field label="Description" errors={at('description')}>
          <textarea className="pw__in" rows={3} value={draft.description} disabled={busy}
                    placeholder="Reads a public chess.com account's games into your graph."
                    onChange={(e) => set({ description: e.target.value })} />
        </Field>
      </section>

      {unplaced.map((e, i) => <p key={i} className="pw__err">{e.message}</p>)}

      <div className="pw__actions">
        <Button onClick={create} loading={busy} disabled={gaps.length > 0}>Create</Button>
        {gaps.length ? <span className="pw__muted pw__small">Still needed: {gaps.join(', ').toLowerCase()}.</span> : null}
      </div>
    </div>
  );
}
